import { SyncOutlined } from '@ant-design/icons';
import {
    Alert,
    Button,
    DatePicker,
    Drawer,
    Form,
    Input,
    Modal,
    PageHeader,
    Radio,
    Select,
    Space,
    Switch,
    Tabs,
    Typography,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import React from 'react';
import { Link } from 'react-router-dom';
import AutocompleteDropdown from '../../components/autocomplete-dropdown';
import DrawerButtons from '../../components/drawer-buttons';
import { DrawerWidth } from '../../config/dimensions';
import Routes from '../../config/routes';
import { DropdownOption } from '../../core/models/DropdownOption';
import { ActionOption, ModuleName } from '../../core/models/enum';
import {
    confirmDropdownHelper,
    filterDropdownOptions,
    getCountriesHelper,
} from '../../helpers/AutocompleteDropdownHelper';
import { isUserAllowed } from '../../helpers/CheckPermissionHelper';
import { dateOnlyFormMoment, formatDateOnly, toDateOnly } from '../../helpers/DateHelper';
import {
    getCategoriesToDropdownOptions,
    getFederationsToDropdownOptions,
} from '../../helpers/FetchAndTransformHelpers';
import { errorWithUserMessage, success } from '../../helpers/NotificationHelper';
import { filterOptionForSearch, normalizeForSearch } from '../../helpers/normalizeForSearch';
import {
    ApiException,
    ArenaEntityCandidateVm,
    CountriesClient,
    CreateMappedClubCommand,
    CreateMappedCompetitionCommand,
    CreateMappedPlayerCommand,
    CreateMappedPlayerFailureVm,
    CreateMappedPlayersCommand,
    CreateMappedTeamCommand,
    BackfillEurocupPlayerNameDobMapsCommand,
    EurocupPlayerNameDobBackfillItemVm,
    ExternalSourceEntitiesClient,
    ExternalSourceEntityTypeEnum,
    ExternalSourceEntityVm,
    ExternalSourceEnum,
    ExternalSourceSuggestionVm,
    MapExternalSourceEntityCommand,
    MvlszCompetitionOptionVm,
    MvlszSeasonOptionVm,
    MicroplusWatchedEventVm,
    RefreshExternalSourceCatalogCommand,
    SetExternalSourceSyncMatchesCommand,
} from '../../utils/api';
import { Props } from './index';
import MicroplusEventPanel from './MicroplusEventPanel';
import ExternalSourceSelect, { externalSourceLabel } from './ExternalSourceSelect';
import { fillSuggestionCache, SuggestionCache } from './suggestionCache';
import NameDobBackfillPreviewModal from './NameDobBackfillPreviewModal';
import CompetitionTeamsBackfill from './CompetitionTeamsBackfill';
import { externalMapsColumnStorageKey, ResizableTable } from './useResizableTableColumns';

type MappedFilter = 'unmapped' | 'mapped' | 'all';

interface State {
    entityType: ExternalSourceEntityTypeEnum;
    mappedFilter: MappedFilter;
    name: string;
    seasonId?: number;
    competitionId?: number;
    loading: boolean;
    refreshing: boolean;
    rows: ExternalSourceEntityVm[];
    mappedCount: number;
    catalogCount: number;
    totalItems: number;
    pageIndex: number;
    pageSize: number;
    federations: DropdownOption[];
    countries: DropdownOption[];
    categories: DropdownOption[];
    selectedFederation?: DropdownOption[];
    selectedCountry?: DropdownOption[];
    suggestions: { [id: number]: ExternalSourceSuggestionVm[] };
    createRow?: ExternalSourceEntityVm;
    createWithoutSquad: boolean;
    createSaving: boolean;
    selectedCategory?: DropdownOption[];
    ivrSeasons: MvlszSeasonOptionVm[];
    ivrCompetitions: MvlszCompetitionOptionVm[];
    tableSeasonId?: number;
    tableCompetitionId?: number;
    tableCompetitions: MvlszCompetitionOptionVm[];
    selectedPlayerIds: number[];
    batchCreating: boolean;
    selectingAll: boolean;
    nameDobBackfilling: boolean;
    nameDobBackfillPreviewOpen: boolean;
    nameDobBackfillPreviewItems: EurocupPlayerNameDobBackfillItemVm[];
    source: ExternalSourceEnum;
    microplusEvent?: MicroplusWatchedEventVm;
}

const COMPETITION_ID_KEY = 'twa.externalMaps.competitionId';
const SEASON_ID_KEY = 'twa.externalMaps.seasonId';

const readStoredCompetitionId = (): number | undefined => {
    try {
        const raw = window.localStorage.getItem(COMPETITION_ID_KEY);
        if (!raw) {
            const legacy = window.localStorage.getItem('twa.externalMaps.headerDefaults');
            if (!legacy) {
                return undefined;
            }
            const parsed = JSON.parse(legacy);
            const competitionId = Number(parsed.competitionId);
            return competitionId > 0 ? competitionId : undefined;
        }
        const competitionId = Number(raw);
        return competitionId > 0 ? competitionId : undefined;
    } catch {
        return undefined;
    }
};

const writeStoredCompetitionId = (competitionId: number): void => {
    try {
        window.localStorage.setItem(COMPETITION_ID_KEY, String(competitionId));
    } catch {
        // quota / private mode: keep working without persistence
    }
};

const readStoredSeasonId = (): number | undefined => {
    try {
        const raw = window.localStorage.getItem(SEASON_ID_KEY);
        if (!raw) {
            return undefined;
        }
        const seasonId = Number(raw);
        return seasonId > 0 ? seasonId : undefined;
    } catch {
        return undefined;
    }
};

const writeStoredSeasonId = (seasonId: number): void => {
    try {
        window.localStorage.setItem(SEASON_ID_KEY, String(seasonId));
    } catch {
        // quota / private mode: keep working without persistence
    }
};

const foldName = (value: string): string => normalizeForSearch(value);

const findHungary = (countries: DropdownOption[]): DropdownOption[] | undefined => {
    const hit = countries.find((country: DropdownOption): boolean => {
        const n = foldName(country.name || '');
        return n === 'hungary' || n === 'magyarorszag';
    });
    return hit ? [hit] : undefined;
};

const rosterCountryCode = (extraJson?: string): string | undefined => {
    if (!extraJson || extraJson.trim().startsWith('{')) {
        return undefined;
    }
    const code = extraJson.trim();
    return code || undefined;
};

const findCountryByCode = (
    countries: DropdownOption[],
    extraJson?: string
): DropdownOption[] | undefined => {
    const code = rosterCountryCode(extraJson);
    if (!code) {
        return undefined;
    }
    const hit = countries.find(
        (country: DropdownOption): boolean =>
            (country.searchableName || '').toLowerCase() === code.toLowerCase()
    );
    return hit ? [hit] : undefined;
};

const competitionLabel = (row: MvlszCompetitionOptionVm): string => row.name || row.shortName || '';

const extraSeason = (extraJson?: string): string | undefined => {
    if (!extraJson) {
        return undefined;
    }
    try {
        const parsed = JSON.parse(extraJson);
        const season = parsed && parsed.season;
        if (typeof season === 'string' && season.trim()) {
            return season.trim();
        }
        return undefined;
    } catch {
        return undefined;
    }
};

const optionId = (options?: DropdownOption[]): number | undefined => {
    if (!options || !options[0]) {
        return undefined;
    }
    const id = Number(options[0].id);
    return id > 0 ? id : undefined;
};

const client = (): ExternalSourceEntitiesClient => new ExternalSourceEntitiesClient();

const apiError = (error: unknown, fallback: string): void => {
    if (error instanceof ApiException) {
        try {
            const parsed = JSON.parse(error.response);
            errorWithUserMessage(parsed.error || error.response);
            return;
        } catch {
            errorWithUserMessage(error.response || fallback);
            return;
        }
    }
    errorWithUserMessage(fallback);
};

class ExternalMaps extends React.PureComponent<Props, State> {
    private lookupRequest = 0;
    private rowsRequest = 0;

    private resetColumnWidths = (): void => {
        // set by ResizableTable when mounted / storage key changes
    };

    private suggestionCache = new SuggestionCache<ExternalSourceSuggestionVm[]>();

    public constructor(props: Props) {
        super(props);

        this.state = {
            entityType: ExternalSourceEntityTypeEnum.Club,
            mappedFilter: 'unmapped',
            name: '',
            loading: false,
            refreshing: false,
            rows: [],
            mappedCount: 0,
            catalogCount: 0,
            totalItems: 0,
            pageIndex: 1,
            pageSize: 50,
            federations: [],
            countries: [],
            categories: [],
            suggestions: {},
            createWithoutSquad: false,
            createSaving: false,
            ivrSeasons: [],
            ivrCompetitions: [],
            tableCompetitions: [],
            selectedPlayerIds: [],
            batchCreating: false,
            selectingAll: false,
            nameDobBackfilling: false,
            nameDobBackfillPreviewOpen: false,
            nameDobBackfillPreviewItems: [],
            source: ExternalSourceEnum.Mvlsz,
        };
    }

    public componentDidMount = (): void => {
        this.loadLookups();
    };

    private isSeasonCatalog = (source: ExternalSourceEnum): boolean =>
        source === ExternalSourceEnum.Eurocup || source === ExternalSourceEnum.ChampionsLeague || source === ExternalSourceEnum.Microplus;

    private sourceLabel = externalSourceLabel;

    private loadLookups = async (): Promise<void> => {
        const request = ++this.lookupRequest;
        const { source } = this.state;
        const storedCompetitionId = readStoredCompetitionId();
        const storedSeasonId = readStoredSeasonId();
        const federations = await getFederationsToDropdownOptions();
        const countries = await getCountriesHelper();
        const countryRows = (await new CountriesClient().getAll()) || [];
        countries.entities.forEach((option: DropdownOption): void => {
            const row = countryRows.find(
                (country): boolean => country.id != null && country.id.toString() === option.id
            );
            option.searchableName = row && row.code ? row.code : '';
        });
        const categories = await getCategoriesToDropdownOptions();
        if (request !== this.lookupRequest || source !== this.state.source) return;
        if (source === ExternalSourceEnum.Microplus) {
            this.setState({ federations: federations.entities, countries: countries.entities, categories: categories.entities });
            return;
        }
        let ivrSeasons: MvlszSeasonOptionVm[] = [];
        try {
            ivrSeasons =
                source === ExternalSourceEnum.ChampionsLeague
                    ? (await client().championsLeagueSeasons()) || []
                    : this.isSeasonCatalog(source)
                    ? (await client().eurocupSeasons()) || []
                    : (await client().mvlszSeasons()) || [];
        } catch {
            ivrSeasons = [];
        }
        const actual = ivrSeasons.find((row: MvlszSeasonOptionVm): boolean => row.isActual);
        const seasonIds = ivrSeasons.map((row: MvlszSeasonOptionVm): number => row.id);
        let seasonId = storedSeasonId;
        if (!seasonId || seasonIds.indexOf(seasonId) < 0) {
            seasonId = actual ? actual.id : ivrSeasons[0] && ivrSeasons[0].id;
        }
        const ivrCompetitions =
            this.isSeasonCatalog(source) && seasonId
                ? [
                      {
                          id: seasonId,
                          name: (
                              ivrSeasons.find(
                                  (row: MvlszSeasonOptionVm): boolean => row.id === seasonId
                              ) || { name: '' }
                          ).name,
                          shortName: '',
                      } as MvlszCompetitionOptionVm,
                  ]
                : seasonId
                ? await this.fetchCompetitions(seasonId)
                : [];
        const competitionIds = ivrCompetitions.map(
            (row: MvlszCompetitionOptionVm): number => row.id
        );
        let competitionId = this.isSeasonCatalog(source) ? seasonId : storedCompetitionId;
        if (!competitionId || competitionIds.indexOf(competitionId) < 0) {
            competitionId = undefined;
        }
        if (request !== this.lookupRequest || source !== this.state.source) return;
        const eurocup = this.isSeasonCatalog(source);
        this.setState(
            {
                federations: federations.entities,
                countries: countries.entities,
                categories: categories.entities,
                ivrSeasons,
                ivrCompetitions,
                seasonId,
                competitionId,
                tableSeasonId: eurocup ? seasonId : this.state.tableSeasonId,
                tableCompetitionId: eurocup ? competitionId : this.state.tableCompetitionId,
                tableCompetitions: eurocup ? ivrCompetitions : this.state.tableCompetitions,
            },
            (): Promise<void> => this.loadRows(1)
        );
        if (seasonId) {
            writeStoredSeasonId(seasonId);
        }
        if (competitionId) {
            writeStoredCompetitionId(competitionId);
        }
    };

    private fetchCompetitions = async (seasonId: number): Promise<MvlszCompetitionOptionVm[]> => {
        try {
            return (await client().mvlszCompetitions(seasonId)) || [];
        } catch {
            return [];
        }
    };

    private changeSeason = async (seasonId: number): Promise<void> => {
        writeStoredSeasonId(seasonId);
        if (this.isSeasonCatalog(this.state.source)) {
            const season = this.state.ivrSeasons.find(
                (row: MvlszSeasonOptionVm): boolean => row.id === seasonId
            );
            const competition = {
                id: seasonId,
                name: season ? season.name : String(seasonId),
                shortName: '',
            } as MvlszCompetitionOptionVm;
            this.setState({ seasonId, ivrCompetitions: [competition], competitionId: seasonId });
            writeStoredCompetitionId(seasonId);
            return;
        }
        this.setState({ seasonId, ivrCompetitions: [], competitionId: undefined });
        const ivrCompetitions = await this.fetchCompetitions(seasonId);
        this.setState({ ivrCompetitions });
    };

    private changeSource = (source: ExternalSourceEnum): void => {
        this.rowsRequest++;
        this.suggestionCache.invalidateAll();
        this.setState({ source, rows: [], suggestions: {}, selectedPlayerIds: [],
            competitionId: undefined, seasonId: undefined, microplusEvent: undefined,
            tableSeasonId: undefined, tableCompetitionId: undefined, tableCompetitions: [],
            ivrSeasons: [], ivrCompetitions: [], mappedCount: 0, catalogCount: 0, totalItems: 0,
            loading: false, refreshing: false,
        }, () => { void this.loadLookups(); });
    };

    private changeMicroplusEvent = (event?: MicroplusWatchedEventVm): void => {
        if (this.state.source !== ExternalSourceEnum.Microplus) return;
        this.rowsRequest++;
        // Membership filter contract: MicroplusEventKey.CatalogId on the server.
        const seasonId = event?.season ? Number(event.season) : undefined;
        const competitionId = event?.event && seasonId
            ? seasonId * 100000 + (event.event.startsWith('ASF|') ? 20000 : 10000) + Number(event.event.split('|')[1])
            : undefined;
        this.suggestionCache.invalidateAll();
        this.setState({ microplusEvent: event, seasonId, competitionId,
            tableSeasonId: seasonId, tableCompetitionId: competitionId,
            rows: [], suggestions: {}, selectedPlayerIds: [], mappedCount: 0, catalogCount: 0,
            totalItems: 0, loading: false, refreshing: false,
        }, () => { if (event) void this.loadRows(1); });
    };

    private mappedQuery = (): boolean | undefined => {
        const { mappedFilter } = this.state;
        if (mappedFilter === 'mapped') {
            return true;
        }
        if (mappedFilter === 'unmapped') {
            return false;
        }
        return undefined;
    };

    private loadRows = async (pageIndex?: number): Promise<void> => {
        const request = ++this.rowsRequest;
        if (this.state.source === ExternalSourceEnum.Microplus && !this.state.microplusEvent) return;
        const { entityType, name, pageSize, tableSeasonId, tableCompetitionId, source, seasonId } =
            this.state;
        const nextPage = pageIndex || this.state.pageIndex;
        const seasonFilter =
            this.isSeasonCatalog(source) ? tableSeasonId || seasonId : tableSeasonId;
        const competitionFilter =
            this.isSeasonCatalog(source)
                ? tableCompetitionId || seasonFilter
                : tableCompetitionId;
        this.setState({ loading: true, pageIndex: nextPage });
        try {
            const result = await client().search(
                source,
                entityType,
                this.mappedQuery(),
                name ? name : undefined,
                seasonFilter,
                competitionFilter,
                nextPage,
                pageSize
            );
            if (request !== this.rowsRequest) return;
            const rows = result.pageItems || [];
            this.setState({
                rows,
                mappedCount: result.mappedCount,
                catalogCount: result.catalogCount,
                totalItems: result.totalItems,
                loading: false,
            });
            this.loadSuggestions(rows);
        } catch (error) {
            if (request !== this.rowsRequest) return;
            this.setState({ loading: false });
            apiError(error, 'Could not load catalog.');
        }
    };

    private publishSuggestions = (): void => {
        this.setState((): Pick<State, 'suggestions'> => ({
            suggestions: this.suggestionCache.snapshot(),
        }));
    };

    private fetchSuggestion = async (id: number): Promise<ExternalSourceSuggestionVm[]> => {
        try {
            return (await client().suggestions(id)) || [];
        } catch {
            return [];
        }
    };

    // Current page only. Cached ids and rows search already marked as having no
    // hit are not requested. Failures are stored as an empty list so the next
    // map does not retry the whole page.
    private loadSuggestions = async (rows: ExternalSourceEntityVm[]): Promise<void> => {
        const changed = await fillSuggestionCache(
            this.suggestionCache,
            rows,
            this.fetchSuggestion,
            []
        );
        if (changed) {
            this.publishSuggestions();
        }
    };

    private dropSuggestionsFor = (row: ExternalSourceEntityVm): void => {
        this.suggestionCache.invalidate([row.id]);
        // Team suggestion lists are filtered by the parent club once it is mapped.
        if (row.entityType === ExternalSourceEntityTypeEnum.Club) {
            this.suggestionCache.invalidateChildren(
                ExternalSourceEntityTypeEnum.Team,
                row.sourceId
            );
        }
    };

    private refreshCatalog = async (): Promise<void> => {
        const { competitionId, source, seasonId, ivrCompetitions, microplusEvent } = this.state;
        if (!competitionId) {
            errorWithUserMessage('Pick a season and a competition first.');
            return;
        }
        this.setState({ refreshing: true });
        try {
            const result = await client().refresh(
                new RefreshExternalSourceCatalogCommand({
                    source,
                    competitionId,
                    event: source === ExternalSourceEnum.Microplus ? microplusEvent?.event : undefined,
                })
            );
            if (source !== this.state.source || microplusEvent !== this.state.microplusEvent) return;
            const extra =
                result.errors && result.errors.length > 0
                    ? ` ${result.errors.length} payload error(s).`
                    : '';
            success(`Added ${result.upserted} rows from ${result.gamesListed} games.${extra}`);
            this.suggestionCache.invalidateAll();
            this.setState(
                {
                    refreshing: false,
                    pageIndex: 1,
                    tableSeasonId: seasonId,
                    tableCompetitionId: competitionId,
                    tableCompetitions: ivrCompetitions,
                },
                (): Promise<void> => this.loadRows(1)
            );
        } catch (error) {
            if (source !== this.state.source || microplusEvent !== this.state.microplusEvent) return;
            this.setState({ refreshing: false });
            apiError(error, 'Add failed.');
        }
    };

    private changeTableSeason = async (seasonId?: number): Promise<void> => {
        if (!seasonId) {
            this.setState(
                {
                    tableSeasonId: undefined,
                    tableCompetitionId: undefined,
                    tableCompetitions: [],
                    pageIndex: 1,
                },
                (): Promise<void> => this.loadRows(1)
            );
            return;
        }
        if (this.isSeasonCatalog(this.state.source)) {
            const season = this.state.ivrSeasons.find(
                (row: MvlszSeasonOptionVm): boolean => row.id === seasonId
            );
            const competition = {
                id: seasonId,
                name: season ? season.name : String(seasonId),
                shortName: '',
            } as MvlszCompetitionOptionVm;
            this.setState(
                {
                    tableSeasonId: seasonId,
                    tableCompetitionId: seasonId,
                    tableCompetitions: [competition],
                    pageIndex: 1,
                },
                (): Promise<void> => this.loadRows(1)
            );
            return;
        }
        this.setState({
            tableSeasonId: seasonId,
            tableCompetitionId: undefined,
            tableCompetitions: [],
            pageIndex: 1,
        });
        const tableCompetitions = await this.fetchCompetitions(seasonId);
        this.setState({ tableCompetitions }, (): Promise<void> => this.loadRows(1));
    };

    private changeTableCompetition = (competitionId?: number): void => {
        this.setState(
            { tableCompetitionId: competitionId, pageIndex: 1 },
            (): Promise<void> => this.loadRows(1)
        );
    };

    private mapRow = async (
        row: ExternalSourceEntityVm,
        arenaEntityId: number,
        confirmSharedArenaTeam?: boolean,
        allowShared?: boolean
    ): Promise<void> => {
        try {
            const mapped = await client().map(
                row.id,
                new MapExternalSourceEntityCommand({
                    id: row.id,
                    arenaEntityId,
                    confirmSharedArenaTeam: !!confirmSharedArenaTeam,
                    allowShared: !!allowShared,
                })
            );
            if (mapped && mapped.mappingNeedsConfirm) {
                if (row.entityType === ExternalSourceEntityTypeEnum.Competition) {
                    const other = (mapped.alreadyMappedFrom || []).join(', ');
                    Modal.confirm({
                        title: 'Arena competition already mapped',
                        content:
                            'This Arena competition is already mapped to '
                            + (other || 'another source row')
                            + '. Map this one to it too?',
                        okText: 'Map this one too',
                        cancelText: 'Cancel',
                        onOk: (): Promise<void> => this.mapRow(row, arenaEntityId, false, true),
                    });
                    return;
                }

                const already = mapped.alreadyMappedFrom || [];
                const arenaName = mapped.arenaDisplayName || 'This Arena team';
                Modal.confirm({
                    title: 'Arena team already mapped',
                    content: (
                        <div>
                            <div>{arenaName} is already mapped from:</div>
                            {already.map(
                                (line: string): React.ReactNode => (
                                    <div key={line}>{line}</div>
                                )
                            )}
                            <div>
                                Map this{' '}
                                {this.sourceLabel(this.state.source)}{' '}
                                team there as well?
                            </div>
                        </div>
                    ),
                    okText: 'Map anyway',
                    cancelText: 'Cancel',
                    onOk: (): Promise<void> => this.mapRow(row, arenaEntityId, true),
                });
                return;
            }
            success('Mapped.');
            this.dropSuggestionsFor(row);
            this.loadRows();
        } catch (error) {
            apiError(error, 'Map failed.');
        }
    };

    private unmapRow = async (row: ExternalSourceEntityVm): Promise<void> => {
        try {
            await client().unmap(row.id);
            success('Unmapped.');
            this.dropSuggestionsFor(row);
            this.loadRows();
        } catch (error) {
            apiError(error, 'Unmap failed.');
        }
    };

    private selectAllCreatable = async (): Promise<void> => {
        const { name, tableSeasonId, tableCompetitionId, source, seasonId } = this.state;
        const seasonFilter =
            this.isSeasonCatalog(source) ? tableSeasonId || seasonId : tableSeasonId;
        const competitionFilter =
            this.isSeasonCatalog(source)
                ? tableCompetitionId || seasonFilter
                : tableCompetitionId;
        this.setState({ selectingAll: true });
        try {
            const ids: number[] = [];
            let page = 1;
            let totalPages = 1;
            while (page <= totalPages) {
                const result = await client().search(
                    source,
                    ExternalSourceEntityTypeEnum.Player,
                    false,
                    name ? name : undefined,
                    seasonFilter,
                    competitionFilter,
                    page,
                    200
                );
                totalPages = result.totalPages || 1;
                (result.pageItems || []).forEach((row: ExternalSourceEntityVm): void => {
                    if (!row.arenaEntityId && this.canCreate(row) && !row.hasSuggestion) {
                        ids.push(row.id);
                    }
                });
                page++;
            }
            this.setState({ selectedPlayerIds: ids, selectingAll: false });
            if (ids.length === 0) {
                errorWithUserMessage(
                    'No unmapped players with a mapped club or team in this filter.'
                );
            }
        } catch (error) {
            this.setState({ selectingAll: false });
            apiError(error, 'Could not select players.');
        }
    };

    private createSelected = async (): Promise<void> => {
        const { selectedPlayerIds } = this.state;
        if (selectedPlayerIds.length === 0) {
            return;
        }

        this.setState({ batchCreating: true });
        let created = 0;
        let skipped = 0;
        const failures: CreateMappedPlayerFailureVm[] = [];
        try {
            for (let index = 0; index < selectedPlayerIds.length; index += 40) {
                const result = await client().createPlayers(
                    new CreateMappedPlayersCommand({
                        ids: selectedPlayerIds.slice(index, index + 40),
                        competitionSourceId:
                            this.state.tableCompetitionId || this.state.competitionId || 0,
                    })
                );
                created += result.created || 0;
                skipped += result.skipped || 0;
                (result.failures || []).forEach((failure: CreateMappedPlayerFailureVm): void => {
                    failures.push(failure);
                });
            }
            const bits = [`Created ${created}.`];
            if (skipped > 0) {
                bits.push(`Skipped ${skipped} already mapped.`);
            }
            if (failures.length > 0) {
                const sample = failures
                    .slice(0, 5)
                    .map(
                        (failure: CreateMappedPlayerFailureVm): string =>
                            `${failure.displayName || '#' + failure.id}: ${
                                failure.reason || 'failed'
                            }`
                    )
                    .join(' ');
                bits.push(`${failures.length} failed. ${sample}`);
            }
            if (failures.length > 0) {
                errorWithUserMessage(bits.join(' '));
            } else {
                success(bits.join(' '));
            }
            this.suggestionCache.invalidate(selectedPlayerIds);
            this.setState({ selectedPlayerIds: [], batchCreating: false });
            this.loadRows();
        } catch (error) {
            this.setState({ batchCreating: false });
            apiError(error, 'Batch create failed.');
            this.suggestionCache.invalidate(selectedPlayerIds);
            this.loadRows();
        }
    };

    private previewEurocupNameDobMaps = async (): Promise<void> => {
        this.setState({ nameDobBackfilling: true });
        try {
            const result = await client().eurocupBackfillPlayerNameDob(
                new BackfillEurocupPlayerNameDobMapsCommand({
                    dryRun: true,
                    externalSourceEntityIds: undefined,
                })
            );
            this.setState({
                nameDobBackfillPreviewOpen: true,
                nameDobBackfillPreviewItems: result.items || [],
            });
        } catch (error) {
            apiError(error, 'Preview failed.');
        } finally {
            this.setState({ nameDobBackfilling: false });
        }
    };

    private applyEurocupNameDobSelected = async (entityIds: number[]): Promise<void> => {
        this.setState({ nameDobBackfilling: true });
        try {
            const result = await client().eurocupBackfillPlayerNameDob(
                new BackfillEurocupPlayerNameDobMapsCommand({
                    dryRun: false,
                    externalSourceEntityIds: entityIds,
                })
            );
            success(`Applied ${result.applied || 0} Euro Cup player map(s).`);
            this.setState({
                nameDobBackfillPreviewOpen: false,
                nameDobBackfillPreviewItems: [],
            });
            this.suggestionCache.invalidateAll();
            await this.loadRows(1);
        } catch (error) {
            apiError(error, 'Apply failed.');
        } finally {
            this.setState({ nameDobBackfilling: false });
        }
    };

    private arenaMapLabel = (displayName?: string, detail?: string): string => {
        if (!displayName) {
            return detail || '';
        }
        return detail ? `${displayName} (${detail})` : displayName;
    };

    private arenaSuggestionProfilePath = (
        entityType: ExternalSourceEntityTypeEnum,
        arenaEntityId: number,
    ): string | null => {
        switch (entityType) {
            case ExternalSourceEntityTypeEnum.Player:
                return Routes.ROUTE_PLAYERS_READ.replace(':playerId', String(arenaEntityId));
            case ExternalSourceEntityTypeEnum.Club:
                return Routes.ROUTE_CLUBS_READ.replace(':clubId', String(arenaEntityId));
            case ExternalSourceEntityTypeEnum.Team:
                return Routes.ROUTE_TEAMS_READ.replace(':teamId', String(arenaEntityId));
            case ExternalSourceEntityTypeEnum.Competition:
                return Routes.ROUTE_COMPETITIONS_READ.replace(
                    ':competitionId',
                    String(arenaEntityId),
                );
            default:
                return null;
        }
    };

    private bareCatalogName = (name?: string): string => {
        if (!name) {
            return '';
        }
        const cut = name.indexOf(' · ');
        return cut > 0 ? name.slice(0, cut) : name;
    };

    private teamCreateName = (row: ExternalSourceEntityVm): string => {
        const bare = this.bareCatalogName(row.displayName);
        const squad = this.readSquad(row.extraJson);
        const parts = [bare, squad.gender, squad.season].filter((part): part is string => !!part);
        return parts.join(' · ');
    };

    private readSquad = (extraJson?: string): { gender?: string; season?: string } => {
        if (!extraJson) {
            return {};
        }
        try {
            const parsed = JSON.parse(extraJson) as { gender?: string; season?: string };
            return {
                gender: this.englishGender(parsed.gender),
                season: parsed.season,
            };
        } catch {
            return {};
        }
    };

    private englishGender = (value?: string): string | undefined => {
        if (!value) {
            return undefined;
        }
        const folded = value
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase();
        if (folded === 'female' || folded === 'noi' || folded === 'leany') {
            return 'female';
        }
        if (folded === 'male' || folded === 'ferfi' || folded === 'fiu') {
            return 'male';
        }
        return undefined;
    };

    private canCreate = (row: ExternalSourceEntityVm): boolean => {
        if (row.entityType === ExternalSourceEntityTypeEnum.Team) {
            return row.parentMapped;
        }
        if (row.entityType === ExternalSourceEntityTypeEnum.Player) {
            return row.parentMapped || row.clubMapped;
        }
        return true;
    };

    private canMapExisting = (row: ExternalSourceEntityVm): boolean => {
        if (row.entityType === ExternalSourceEntityTypeEnum.Team) {
            return row.parentMapped;
        }
        return true;
    };

    private arenaOptions = async (
        value: string,
        row: ExternalSourceEntityVm
    ): Promise<DropdownOption[]> => {
        const clubId =
            row.entityType === ExternalSourceEntityTypeEnum.Team
                ? row.parentArenaEntityId
                : undefined;
        const results = await client().arenaSearch(row.entityType, value, clubId);
        return (results || []).map(
            (item: ArenaEntityCandidateVm): DropdownOption => ({
                id: item.id.toString(),
                name: this.arenaMapLabel(item.displayName, item.detail),
            })
        );
    };

    private openCreate = (row: ExternalSourceEntityVm, withoutSquad?: boolean): void => {
        const { countries } = this.state;
        this.setState({
            createRow: row,
            createWithoutSquad: !!withoutSquad,
            selectedCategory: undefined,
            selectedFederation: undefined,
            selectedCountry:
                this.isSeasonCatalog(this.state.source)
                    ? findCountryByCode(countries, row.extraJson)
                    : findHungary(countries),
        });
    };

    private closeCreate = (): void => {
        this.setState({
            createRow: undefined,
            createWithoutSquad: false,
            selectedCategory: undefined,
            selectedFederation: undefined,
            selectedCountry: undefined,
            createSaving: false,
        });
    };

    private submitCreate = async (values: any): Promise<void> => {
        const { createRow, selectedCategory, selectedFederation, selectedCountry } = this.state;
        if (!createRow) {
            return;
        }

        this.setState({ createSaving: true });
        try {
            if (createRow.entityType === ExternalSourceEntityTypeEnum.Club) {
                await client().createClub(
                    createRow.id,
                    new CreateMappedClubCommand({
                        id: createRow.id,
                        name: values.name,
                        nameUniversal: values.nameUniversal,
                        shortName: values.shortName,
                        federationId: undefined,
                        countryId: optionId(selectedCountry),
                    })
                );
            } else if (createRow.entityType === ExternalSourceEntityTypeEnum.Team) {
                const categoryId =
                    selectedCategory && selectedCategory[0]
                        ? Number(selectedCategory[0].id)
                        : values.categoryId;
                if (!categoryId) {
                    errorWithUserMessage('Pick a category.');
                    this.setState({ createSaving: false });
                    return;
                }
                await client().createTeam(
                    createRow.id,
                    new CreateMappedTeamCommand({
                        id: createRow.id,
                        name: values.name,
                        nameUniversal: values.nameUniversal,
                        shortName: values.shortName,
                        categoryId,
                        countryId: optionId(selectedCountry),
                    })
                );
            } else if (createRow.entityType === ExternalSourceEntityTypeEnum.Player) {
                await client().createPlayer(
                    createRow.id,
                    new CreateMappedPlayerCommand({
                        id: createRow.id,
                        name: values.name,
                        surname: values.surname,
                        birthDate: toDateOnly(values.birthDate as moment.Moment),
                        dominantHand: values.dominantHand,
                        competitionSourceId:
                            this.state.tableCompetitionId || this.state.competitionId || 0,
                        allowWithoutSquad: this.state.createWithoutSquad,
                    })
                );
            } else {
                const federationId = optionId(selectedFederation);
                if (!federationId) {
                    errorWithUserMessage('Pick a federation.');
                    this.setState({ createSaving: false });
                    return;
                }
                await client().createCompetition(
                    createRow.id,
                    new CreateMappedCompetitionCommand({
                        id: createRow.id,
                        name: values.name,
                        nameUniversal: values.nameUniversal,
                        shortName: values.shortName,
                        season: values.season,
                        federationId,
                        countryId: optionId(selectedCountry),
                        categoryId: undefined,
                    })
                );
            }
            success('Created and mapped.');
            this.dropSuggestionsFor(createRow);
            this.closeCreate();
            this.loadRows();
        } catch (error) {
            this.setState({ createSaving: false });
            apiError(error, 'Create failed.');
        }
    };

    private setSyncMatches = async (
        row: ExternalSourceEntityVm,
        syncMatches: boolean
    ): Promise<void> => {
        try {
            await client().syncMatches(
                row.id,
                new SetExternalSourceSyncMatchesCommand({
                    id: row.id,
                    syncMatches,
                })
            );
            success(syncMatches ? 'Match sync is on.' : 'Match sync is off.');
            this.loadRows();
        } catch (error) {
            apiError(error, 'Could not change match sync.');
            this.loadRows();
        }
    };

    private identityText = (row: ExternalSourceEntityVm): React.ReactNode => {
        const { ivrCompetitions } = this.state;
        const bits: string[] = [];
        if (!this.isSeasonCatalog(this.state.source) && row.sourceId) {
            bits.push(`#${row.sourceId}`);
        }
        bits.push(row.displayName || '');
        const country = rosterCountryCode(row.extraJson);
        if (this.isSeasonCatalog(this.state.source) && country) {
            bits.push(country);
        }
        if (row.entityType === ExternalSourceEntityTypeEnum.Player) {
            if (row.birthDate) {
                bits.push(formatDateOnly(row.birthDate, 'YYYY-MM-DD'));
            }
            if (row.registrationNumber) {
                bits.push(row.registrationNumber);
            }
        }
        if (row.entityType === ExternalSourceEntityTypeEnum.Competition) {
            const fromExtra = extraSeason(row.extraJson);
            const fromHeader = (ivrCompetitions || []).find(
                (item: MvlszCompetitionOptionVm): boolean => item.id === row.sourceId
            );
            const season = fromExtra || (fromHeader && fromHeader.seasonName) || undefined;
            if (season) {
                bits.push(season);
            }
        }
        return bits.filter(Boolean).join(' · ');
    };

    private columns = (): ColumnsType<ExternalSourceEntityVm> => {
        const { suggestions, entityType, source } = this.state;
        return [
            {
                title: this.sourceLabel(source),
                key: 'hungary',
                width: 280,
                render: (_: unknown, row: ExternalSourceEntityVm): React.ReactNode => (
                    <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        <Typography.Text ellipsis={{ tooltip: true }}>
                            {this.identityText(row)}
                        </Typography.Text>
                        {row.parentDisplayName && (
                            <Typography.Text
                                type="secondary"
                                style={{ display: 'block', whiteSpace: 'normal', wordBreak: 'break-word' }}
                            >
                                Parent: {row.parentDisplayName}
                                {row.parentMapped ? ' (mapped)' : ' (unmapped)'}
                            </Typography.Text>
                        )}
                    </div>
                ),
            },
            ...(entityType === ExternalSourceEntityTypeEnum.Competition
                ? [
                      {
                          title: 'Sync matches',
                          key: 'sync',
                          width: 130,
                          render: (_: unknown, row: ExternalSourceEntityVm): React.ReactNode => (
                              <Switch
                                  checked={!!row.syncMatches}
                                  disabled={!row.arenaEntityId}
                                  onChange={(checked: boolean): Promise<void> =>
                                      this.setSyncMatches(row, checked)
                                  }
                              />
                          ),
                      },
                  ]
                : []),
            {
                title: 'Arena',
                key: 'arena',
                width: 220,
                render: (_: unknown, row: ExternalSourceEntityVm): React.ReactNode => {
                    const label = row.arenaEntityId
                        ? `${row.arenaDisplayName || ''} (#${row.arenaEntityId})`
                        : '-';
                    return (
                        <Typography.Text ellipsis={{ tooltip: true }} style={{ width: '100%' }}>
                            {label}
                        </Typography.Text>
                    );
                },
            },
            {
                title: 'Suggestion',
                key: 'suggestion',
                width: 320,
                render: (_: unknown, row: ExternalSourceEntityVm): React.ReactNode => {
                    const hits = suggestions[row.id] || [];
                    if (row.arenaEntityId || hits.length === 0) {
                        return '-';
                    }
                    const shown =
                        entityType === ExternalSourceEntityTypeEnum.Team
                            ? hits.slice(0, 3)
                            : entityType === ExternalSourceEntityTypeEnum.Player
                              ? hits.slice(0, 5)
                              : hits.slice(0, 1);
                    const stackSuggestions =
                        entityType === ExternalSourceEntityTypeEnum.Player ||
                        entityType === ExternalSourceEntityTypeEnum.Team;
                    return (
                        <div
                            style={
                                stackSuggestions
                                    ? {
                                          display: 'flex',
                                          flexDirection: 'column',
                                          alignItems: 'stretch',
                                          gap: 6,
                                          width: '100%',
                                      }
                                    : undefined
                            }
                        >
                            {shown.map(
                                (hit): React.ReactNode => {
                                    const profilePath = this.arenaSuggestionProfilePath(
                                        entityType,
                                        hit.arenaEntityId,
                                    );
                                    return (
                                    <Button
                                        key={hit.arenaEntityId}
                                        size="small"
                                        block={stackSuggestions}
                                        disabled={!this.canMapExisting(row)}
                                        style={{
                                            height: 'auto',
                                            whiteSpace: 'normal',
                                            textAlign: 'left',
                                        }}
                                        onClick={(): Promise<void> =>
                                            this.mapRow(row, hit.arenaEntityId)
                                        }
                                    >
                                        <div>
                                            Map {this.arenaMapLabel(hit.displayName, hit.detail)}{' '}
                                            {profilePath ? (
                                                <Link
                                                    to={profilePath}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(
                                                        event: React.MouseEvent,
                                                    ): void => {
                                                        event.stopPropagation();
                                                    }}
                                                >
                                                    #{hit.arenaEntityId}
                                                </Link>
                                            ) : (
                                                <>#{hit.arenaEntityId}</>
                                            )}
                                            {hit.scoreReason ? (
                                                <div
                                                    style={{
                                                        fontSize: 11,
                                                        opacity: 0.85,
                                                        fontWeight: 'normal',
                                                    }}
                                                >
                                                    {hit.scoreReason}
                                                </div>
                                            ) : null}
                                        </div>
                                    </Button>
                                    );
                                }
                            )}
                        </div>
                    );
                },
            },
            {
                title: 'Map existing',
                key: 'map',
                width: 260,
                render: (_: unknown, row: ExternalSourceEntityVm): React.ReactNode => {
                    if (row.arenaEntityId) {
                        return (
                            <Button size="small" onClick={(): Promise<void> => this.unmapRow(row)}>
                                Unmap
                            </Button>
                        );
                    }
                    if (!this.canMapExisting(row)) {
                        return (
                            <Typography.Text type="secondary">
                                Map parent club first
                            </Typography.Text>
                        );
                    }
                    return (
                        <AutocompleteDropdown
                            placeholder="Search Arena"
                            numberOfCharMinSearch={2}
                            getOptions={(value: string): Promise<DropdownOption[]> =>
                                this.arenaOptions(value, row)
                            }
                            confirmDirty={(options: DropdownOption[]): void => {
                                const picked = confirmDropdownHelper(options);
                                if (picked && picked[0]) {
                                    this.mapRow(row, Number(picked[0].id));
                                }
                            }}
                            style={{ width: '100%' }}
                            isAllowedToClear
                        />
                    );
                },
            },
            {
                title: '',
                key: 'create',
                width: 110,
                render: (_: unknown, row: ExternalSourceEntityVm): React.ReactNode => {
                    if (row.arenaEntityId) {
                        return null;
                    }
                    if (!this.canCreate(row)) {
                        if (entityType === ExternalSourceEntityTypeEnum.Player) {
                            return (
                                <Button
                                    size="small"
                                    onClick={(): void => this.openCreate(row, true)}
                                >
                                    Create without team
                                </Button>
                            );
                        }
                        return (
                            <Typography.Text type="secondary">
                                Map parent club first
                            </Typography.Text>
                        );
                    }
                    return (
                        <Button
                            size="small"
                            type="primary"
                            onClick={(): void => this.openCreate(row)}
                        >
                            Create
                        </Button>
                    );
                },
            },
        ];
    };

    private tabHint = (): string => {
        const { entityType, source } = this.state;
        if (entityType === ExternalSourceEntityTypeEnum.Club) {
            return 'Start with clubs. No parent is required. Map an existing Arena club, or Create.';
        }
        if (entityType === ExternalSourceEntityTypeEnum.Team) {
            return 'Teams need the parent club mapped first. Create puts the new team under that club; pick men senior (or the right category).';
        }
        if (entityType === ExternalSourceEntityTypeEnum.Player) {
            return this.isSeasonCatalog(source)
                ? 'Players need the parent team for this season mapped (or at least the club). Rows with a suggestion stay on the first page and cannot be ticked. Map those with the suggestion button. Tick the rest, or use Select all unmapped, then Create selected. That uses the roster first name, surname, and birth date.'
                : 'Players need the parent team mapped (or at least the club). Rows with a suggestion stay on the first page and cannot be ticked. Map those with the suggestion button. Tick the rest, or use Select all unmapped, then Create selected. That uses the Hungary first name, surname, birth date, and hand.';
        }
        if (source === ExternalSourceEnum.Microplus) {
            return 'Main and qualification competitions are selected by event configuration. Map clubs, teams and players before importing matches.';
        }
        if (source === ExternalSourceEnum.ChampionsLeague) {
            return 'Qualification and the group stage are separate competitions in this season. Map each one, then turn Sync matches on. Import attaches to an existing match in that competition when the same teams already have one.';
        }
        return this.isSeasonCatalog(source)
            ? 'One competition per season. Filter by season, map it or create it, and pick the federation yourself. Sync matches is stored after the competition is mapped. The timer is not running yet.'
            : 'Competition rows are every Hungarian competition from seasons you have added. Filter by season to work one season. Sync matches is only available after the competition is mapped. The timer walks checked competitions only.';
    };

    private createTitle = (): string => {
        const { createRow } = this.state;
        if (!createRow) {
            return 'Create';
        }
        if (createRow.entityType === ExternalSourceEntityTypeEnum.Club) {
            return 'Create club';
        }
        if (createRow.entityType === ExternalSourceEntityTypeEnum.Team) {
            return 'Create team';
        }
        if (createRow.entityType === ExternalSourceEntityTypeEnum.Player) {
            return this.state.createWithoutSquad ? 'Create player without team' : 'Create player';
        }
        return 'Create competition';
    };

    private renderCreateForm = (): React.ReactNode => {
        const {
            createRow,
            createSaving,
            categories,
            selectedCategory,
            countries,
            selectedCountry,
            federations,
            selectedFederation,
            source,
            createWithoutSquad,
        } = this.state;
        if (!createRow) {
            return null;
        }
        const catalogName =
            createRow.entityType === ExternalSourceEntityTypeEnum.Team
                ? this.teamCreateName(createRow)
                : createRow.displayName;

        if (createRow.entityType === ExternalSourceEntityTypeEnum.Player) {
            return (
                <Form layout="vertical" onFinish={this.submitCreate}>
                    {createWithoutSquad && (
                        <Typography.Paragraph type="secondary">
                            {this.isSeasonCatalog(source)
                                ? 'This player is not on a squad in the roster. This saves the Arena player with no club and no squad.'
                                : 'Hungary did not put this player on a team. This saves the Arena player with no club and no squad.'}
                        </Typography.Paragraph>
                    )}
                    <Form.Item
                        name="name"
                        label="First name"
                        initialValue={createRow.firstName}
                        rules={[{ required: true, message: 'Required' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="surname"
                        label="Surname"
                        initialValue={createRow.lastName}
                        rules={[{ required: true, message: 'Required' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="birthDate"
                        label="Birth date"
                        initialValue={
                            createRow.birthDate ? dateOnlyFormMoment(createRow.birthDate) : undefined
                        }
                        rules={[{ required: true, message: 'Required' }]}
                    >
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="dominantHand" label="Dominant hand" extra="R or L">
                        <Input maxLength={1} />
                    </Form.Item>
                    <DrawerButtons isSaving={createSaving} onCancelAction={this.closeCreate} />
                </Form>
            );
        }

        return (
            <Form layout="vertical" onFinish={this.submitCreate}>
                <Form.Item
                    name="name"
                    label="Name"
                    initialValue={catalogName}
                    rules={[{ required: true, message: 'Required' }]}
                >
                    <Input />
                </Form.Item>
                <Form.Item
                    name="nameUniversal"
                    label="Universal name"
                    initialValue={catalogName}
                    rules={[{ required: true, message: 'Required' }]}
                >
                    <Input />
                </Form.Item>
                <Form.Item name="shortName" label="Short name" initialValue={createRow.shortName}>
                    <Input />
                </Form.Item>
                <Form.Item
                    label="Country"
                    extra={
                        this.isSeasonCatalog(source)
                            ? rosterCountryCode(createRow.extraJson)
                                ? `Roster country ${rosterCountryCode(
                                      createRow.extraJson
                                  )}. Pick that country. You pick the federation.`
                                : 'Pick the country. You pick the federation.'
                            : 'Defaults to Hungary. Change here only if this row is not Hungarian.'
                    }
                >
                    <AutocompleteDropdown
                        placeholder="Choose country"
                        getOptionsFrontend={(value: string): DropdownOption[] =>
                            filterDropdownOptions(value, countries)
                        }
                        initialValues={selectedCountry}
                        confirmDirty={(options: DropdownOption[]): void =>
                            this.setState({
                                selectedCountry: confirmDropdownHelper(options),
                            })
                        }
                        style={{ width: '100%' }}
                        isAllowedToClear
                    />
                </Form.Item>
                {createRow.entityType === ExternalSourceEntityTypeEnum.Team && (
                    <Form.Item label="Category" required>
                        <AutocompleteDropdown
                            placeholder="Choose category"
                            getOptionsFrontend={(value: string): DropdownOption[] =>
                                filterDropdownOptions(value, categories)
                            }
                            initialValues={selectedCategory}
                            confirmDirty={(options: DropdownOption[]): void =>
                                this.setState({
                                    selectedCategory: confirmDropdownHelper(options),
                                })
                            }
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                )}
                {createRow.entityType === ExternalSourceEntityTypeEnum.Competition && (
                    <>
                        <Form.Item
                            label="Federation"
                            required
                            extra="Needed only when creating a competition."
                        >
                            <AutocompleteDropdown
                                placeholder="Choose federation"
                                getOptionsFrontend={(value: string): DropdownOption[] =>
                                    filterDropdownOptions(value, federations)
                                }
                                initialValues={selectedFederation}
                                confirmDirty={(options: DropdownOption[]): void =>
                                    this.setState({
                                        selectedFederation: confirmDropdownHelper(options),
                                    })
                                }
                                style={{ width: '100%' }}
                                isAllowedToClear
                            />
                        </Form.Item>
                        <Form.Item
                            name="season"
                            label="Season"
                            rules={[{ required: true, message: 'Required' }]}
                            initialValue={extraSeason(createRow.extraJson)}
                        >
                            <Input placeholder="2025/26" />
                        </Form.Item>
                    </>
                )}
                <DrawerButtons isSaving={createSaving} onCancelAction={this.closeCreate} />
            </Form>
        );
    };

    public render(): React.ReactElement {
        const { userProfile } = this.props;
        const {
            entityType,
            mappedFilter,
            name,
            competitionId,
            loading,
            refreshing,
            rows,
            mappedCount,
            catalogCount,
            totalItems,
            pageIndex,
            pageSize,
            createRow,
            seasonId,
            ivrSeasons,
            ivrCompetitions,
            tableSeasonId,
            tableCompetitionId,
            tableCompetitions,
            selectedPlayerIds,
            batchCreating,
            selectingAll,
            nameDobBackfilling,
            source,
        } = this.state;

        const allowed = isUserAllowed(userProfile, ModuleName.ExternalMaps, ActionOption.Read);

        return (
            <div>
                <PageHeader title="External sources" style={{ padding: '16px 0px' }} />
                {!allowed ? (
                    <Typography.Text>Admin only.</Typography.Text>
                ) : (
                    <>
                        <CompetitionTeamsBackfill />
                        <ExternalSourceSelect value={source} onChange={this.changeSource} />
                        {source === ExternalSourceEnum.Microplus ? <Alert type="info" showIcon style={{ marginBottom: 16 }}
                            message="Pick an event, add its catalog, then map clubs, teams and players before importing games. Main and qualification competitions come from event configuration." /> : <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message={
                                source === ExternalSourceEnum.ChampionsLeague
                                    ? 'Do this in order. This page maps Champions League clubs, one squad per club per season, players, and separate qualification and group competitions. It does not create matches.'
                                    : source === ExternalSourceEnum.Eurocup
                                    ? 'Do this in order. This page maps Euro Cup clubs, one squad per club per season, players, and one competition per season. It does not create matches.'
                                    : 'Do this in order. This page only maps Hungary ids to Arena clubs, teams, players, and one competition. It does not create matches, lineups, or events.'
                            }
                            description={
                                <ol style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                                    <li>
                                        {this.isSeasonCatalog(source) ? (
                                            <>
                                                Pick a <b>season</b>. 2026/27 is marked current.
                                                <b> Add season data to table</b> pulls that season's
                                                clubs, squads, and players. Country comes from the
                                                roster. You pick the federation when you create a
                                                competition.
                                                {source === ExternalSourceEnum.ChampionsLeague
                                                    ? ' Qualification and the group stage are two rows on the Competition tab.'
                                                    : ''}
                                            </>
                                        ) : (
                                            <>
                                                Pick a <b>season</b>, then a{' '}
                                                <b>competition by name</b>. Adding it pulls that
                                                competition's clubs, teams, and the public
                                                registered-player list, and also lists the other
                                                competitions in that season on the Competition tab.
                                                Country defaults to Hungary in each Create drawer;
                                                federation is asked only when creating a
                                                competition.
                                            </>
                                        )}
                                    </li>
                                    <li>
                                        Click{' '}
                                        <b>
                                            {this.isSeasonCatalog(source)
                                                ? 'Add season data to table'
                                                : 'Add competition data to table'}
                                        </b>
                                        . The filters under the tabs then show that season
                                        {this.isSeasonCatalog(source)
                                            ? ''
                                            : ' and competition'}
                                        . Adding again never clears a map you already confirmed.
                                        Data added before these filters existed needs one more add
                                        before a season or competition filter can see it.
                                    </li>
                                    <li>
                                        <b>Clubs first.</b> Prefer Map existing when the Arena club
                                        already exists; otherwise Create. Finish clubs before teams.
                                    </li>
                                    <li>
                                        <b>Teams next.</b> The parent club on the row must already
                                        be mapped, or Map/Create stays blocked. Create needs a
                                        category and puts the team under that mapped club.
                                    </li>
                                    <li>
                                        <b>Players last.</b> Map the parent team (or at least the
                                        club) first. Create makes an Arena person + player and
                                        attaches them to that mapped club/team. Map existing does
                                        the same squad attachment when that parent team is already
                                        mapped. That is how the player is tied to a team here.
                                    </li>
                                    <li>
                                        <b>Competition</b> tab: filter by season to see that
                                        season's competitions. Map the one you need, or Create a new
                                        season (federation is asked only there).
                                    </li>
                                    <li>
                                        Suggestions are hints; you still confirm Map. Unmap only
                                        drops the link; it does not delete the Arena record. Polish
                                        names later on the normal Club / Team / Player / Competition
                                        pages.
                                    </li>
                                </ol>
                            }
                        />}
                        {source === ExternalSourceEnum.Microplus && <MicroplusEventPanel onChange={this.changeMicroplusEvent} />}
                        <Space wrap style={{ marginBottom: 16 }}>
                            <ExternalSourceSelect value={source} disabled />
                            {source !== ExternalSourceEnum.Microplus && <>
                                <Select
                                    showSearch
                                    filterOption={filterOptionForSearch}
                                    style={{ width: 160 }}
                                    placeholder="Season"
                                    value={seasonId}
                                    optionFilterProp="children"
                                    onChange={(value: number): Promise<void> =>
                                        this.changeSeason(value)
                                    }
                                >
                                    {ivrSeasons.map(
                                        (row: MvlszSeasonOptionVm): React.ReactElement => (
                                            <Select.Option key={row.id} value={row.id}>
                                                {row.name}
                                            </Select.Option>
                                        )
                                    )}
                                </Select>
                                <Select
                                    showSearch
                                    filterOption={filterOptionForSearch}
                                    style={{ minWidth: 320, maxWidth: 480 }}
                                    placeholder="Competition"
                                    value={competitionId}
                                    disabled={!seasonId}
                                    optionFilterProp="children"
                                    onChange={(value: number): void => {
                                        this.setState({ competitionId: value });
                                        writeStoredCompetitionId(value);
                                    }}
                                >
                                    {ivrCompetitions.map(
                                        (row: MvlszCompetitionOptionVm): React.ReactElement => (
                                            <Select.Option key={row.id} value={row.id}>
                                                {competitionLabel(row)}
                                            </Select.Option>
                                        )
                                    )}
                                </Select>
                            </>}
                            <Button
                                type="primary"
                                onClick={this.refreshCatalog}
                                disabled={refreshing || !competitionId}
                            >
                                {refreshing ? <SyncOutlined spin /> : null}{' '}
                                {source === ExternalSourceEnum.Microplus ? 'Add event data to table' : this.isSeasonCatalog(source)
                                    ? 'Add season data to table'
                                    : 'Add competition data to table'}
                            </Button>
                        </Space>

                        <Tabs
                            activeKey={String(entityType)}
                            onChange={(key: string): void =>
                                this.setState(
                                    {
                                        entityType: Number(key) as ExternalSourceEntityTypeEnum,
                                        pageIndex: 1,
                                        selectedPlayerIds: [],
                                    },
                                    (): Promise<void> => this.loadRows(1)
                                )
                            }
                        >
                            <Tabs.TabPane
                                tab="Clubs"
                                key={String(ExternalSourceEntityTypeEnum.Club)}
                            />
                            <Tabs.TabPane
                                tab="Teams"
                                key={String(ExternalSourceEntityTypeEnum.Team)}
                            />
                            <Tabs.TabPane
                                tab="Players"
                                key={String(ExternalSourceEntityTypeEnum.Player)}
                            />
                            <Tabs.TabPane
                                tab="Competition"
                                key={String(ExternalSourceEntityTypeEnum.Competition)}
                            />
                        </Tabs>
                        <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
                            {this.tabHint()}
                        </Typography.Paragraph>

                        <Space wrap style={{ marginBottom: 16 }}>
                            {source !== ExternalSourceEnum.Microplus && <>
                                <Select
                                    allowClear
                                    showSearch
                                    filterOption={filterOptionForSearch}
                                    style={{ width: 160 }}
                                    placeholder="All seasons"
                                    value={tableSeasonId}
                                    optionFilterProp="children"
                                    onChange={(value?: number): Promise<void> =>
                                        this.changeTableSeason(value)
                                    }
                                >
                                    {ivrSeasons.map(
                                        (row: MvlszSeasonOptionVm): React.ReactElement => (
                                            <Select.Option key={row.id} value={row.id}>
                                                {row.name}
                                            </Select.Option>
                                        )
                                    )}
                                </Select>
                                <Select
                                    allowClear
                                    showSearch
                                    filterOption={filterOptionForSearch}
                                    style={{ minWidth: 280, maxWidth: 420 }}
                                    placeholder="All competitions"
                                    value={tableCompetitionId}
                                    disabled={!tableSeasonId}
                                    optionFilterProp="children"
                                    onChange={(value?: number): void =>
                                        this.changeTableCompetition(value)
                                    }
                                >
                                    {tableCompetitions.map(
                                        (row: MvlszCompetitionOptionVm): React.ReactElement => (
                                            <Select.Option key={row.id} value={row.id}>
                                                {competitionLabel(row)}
                                            </Select.Option>
                                        )
                                    )}
                                </Select>
                            </>}
                            <Radio.Group
                                value={mappedFilter}
                                onChange={(e): void =>
                                    this.setState(
                                        { mappedFilter: e.target.value, pageIndex: 1 },
                                        (): Promise<void> => this.loadRows(1)
                                    )
                                }
                            >
                                <Radio.Button value="unmapped">Unmapped</Radio.Button>
                                <Radio.Button value="mapped">Mapped</Radio.Button>
                                <Radio.Button value="all">All</Radio.Button>
                            </Radio.Group>
                            <Input.Search
                                allowClear
                                placeholder="Name"
                                style={{ width: 240 }}
                                value={name}
                                onChange={(e): void => this.setState({ name: e.target.value })}
                                onSearch={(): Promise<void> => this.loadRows(1)}
                            />
                            <Typography.Text type="secondary">
                                {mappedCount}/{catalogCount} mapped
                            </Typography.Text>
                            <Button
                                type="link"
                                size="small"
                                onClick={(): void => this.resetColumnWidths()}
                            >
                                Reset column widths
                            </Button>
                            {entityType === ExternalSourceEntityTypeEnum.Player &&
                                source === ExternalSourceEnum.Eurocup && (
                                    <>
                                        <Button
                                            onClick={(): Promise<void> =>
                                                this.previewEurocupNameDobMaps()
                                            }
                                            disabled={nameDobBackfilling || loading}
                                        >
                                            Preview name+DOB maps
                                        </Button>
                                    </>
                                )}
                            {entityType === ExternalSourceEntityTypeEnum.Player && (
                                <>
                                    <Button
                                        onClick={(): Promise<void> => this.selectAllCreatable()}
                                        disabled={selectingAll || batchCreating || loading}
                                    >
                                        Select all unmapped
                                    </Button>
                                    <Button
                                        type="primary"
                                        onClick={(): Promise<void> => this.createSelected()}
                                        disabled={
                                            batchCreating ||
                                            selectingAll ||
                                            selectedPlayerIds.length === 0
                                        }
                                    >
                                        {batchCreating
                                            ? 'Creating...'
                                            : `Create selected (${selectedPlayerIds.length})`}
                                    </Button>
                                </>
                            )}
                        </Space>

                        <ResizableTable
                            storageKey={externalMapsColumnStorageKey(entityType)}
                            onRegisterReset={(reset): void => {
                                this.resetColumnWidths = reset;
                            }}
                            rowKey="id"
                            loading={loading || refreshing || batchCreating}
                            dataSource={rows}
                            columns={this.columns()}
                            rowSelection={
                                entityType === ExternalSourceEntityTypeEnum.Player
                                    ? {
                                          selectedRowKeys: selectedPlayerIds,
                                          onChange: (keys: React.Key[]): void => {
                                              const pageIds = rows.map(
                                                  (row: ExternalSourceEntityVm): number => row.id
                                              );
                                              const kept = selectedPlayerIds.filter(
                                                  (id: number): boolean => pageIds.indexOf(id) < 0
                                              );
                                              this.setState({
                                                  selectedPlayerIds: kept.concat(
                                                      keys.map((key: React.Key): number =>
                                                          Number(key)
                                                      )
                                                  ),
                                              });
                                          },
                                          getCheckboxProps: (
                                              row: ExternalSourceEntityVm
                                          ): { disabled: boolean } => ({
                                              disabled:
                                                  !!row.arenaEntityId ||
                                                  !this.canCreate(row) ||
                                                  !!row.hasSuggestion ||
                                                  (this.state.suggestions[row.id] || []).length > 0,
                                          }),
                                      }
                                    : undefined
                            }
                            pagination={{
                                current: pageIndex,
                                pageSize,
                                total: totalItems,
                                onChange: (page: number): Promise<void> => this.loadRows(page),
                            }}
                        />

                        <Drawer
                            title={this.createTitle()}
                            visible={!!createRow}
                            onClose={this.closeCreate}
                            width={DrawerWidth}
                            destroyOnClose
                        >
                            {this.renderCreateForm()}
                        </Drawer>

                        <NameDobBackfillPreviewModal
                            open={this.state.nameDobBackfillPreviewOpen}
                            items={this.state.nameDobBackfillPreviewItems}
                            applying={this.state.nameDobBackfilling}
                            onClose={(): void =>
                                this.setState({
                                    nameDobBackfillPreviewOpen: false,
                                    nameDobBackfillPreviewItems: [],
                                })
                            }
                            onApply={this.applyEurocupNameDobSelected}
                        />
                    </>
                )}
            </div>
        );
    }
}

export default ExternalMaps;
